import {
  collection,
  getDocs,
  query,
  setDoc,
  getDoc,
  doc,
  deleteDoc,
  runTransaction,
  where,
  or,
  endAt, limit
} from "@firebase/firestore";
import {firestore} from "@/services/firebase.service";
import {WordQueryType, WordType} from "@/types/words";
import cookieService from "@/services/cookie.service";


const listWords = async (wordQuery?: WordQueryType): Promise<WordType[]> => {
  const wordsCollection  = collection(firestore, 'words')

  const constraints = []
  if (wordQuery?.isDeleted !== undefined) constraints.push(where('isDeleted', '==', wordQuery.isDeleted))
  else constraints.push(where('isDeleted', '==', false))
  if (wordQuery?.isApproved !== undefined) constraints.push(where('isApproved', '==', wordQuery.isApproved))
  if (wordQuery?.word) constraints.push(where('word', '==', wordQuery.word))
  if (wordQuery?.category) constraints.push(where('category', '==', wordQuery.category))
  // Only approved would be query(wordsCollection, where('isApproved', '==', true))
  const q = query(wordsCollection, ...constraints, )

  const snapshots = await getDocs(q)

  return snapshots.docs.map(doc => ({word: doc.id, ...doc.data()} as WordType))

}


const getWord = async (word: string): Promise<WordType | null> => {
  const wordsCollection  = collection(firestore, 'words')

  const docRef = doc(wordsCollection, word.toLowerCase())
  const docSnap = await getDoc(docRef)


  return docSnap.exists() ? {word: docSnap.id, ...docSnap.data()} as WordType : null
}

const createWord = async (word: WordType): Promise<WordType> => {
  word.word = word.word.toLowerCase()
  const wordsCollection  = collection(firestore, 'words')

  const newDocRef = doc(wordsCollection, word.word)
  await setDoc(newDocRef, {
    ...word,
    createdBy: word.createdBy || cookieService.get('displayName'),
    createdAt: word.createdAt || new Date(),
    updatedAt: word.updatedAt || new Date(),
  })


  const newDoc = await getDoc(newDocRef)

  return {word: newDoc.id, ...newDoc.data()} as WordType
}

const updateWord = async (unfilteredWord: string, data: Partial<WordType>): Promise<WordType> => {
  const word = unfilteredWord.toLowerCase()
  const wordsCollection  = collection(firestore, 'words')

  if (data.word) data.word = data.word.toLowerCase()

  const docRef = doc(wordsCollection, word)
  await setDoc(docRef, {
    ...data,
    updatedBy: cookieService.get('displayName'),
    updatedAt: new Date(),
  }, {merge: true})

  if (data.word && word !== data.word) {
    const existingWord = await getWord(data.word)
    if (existingWord) throw new Error('Word already exists')
    const dataToCopy = await getWord(word)
    if (dataToCopy) {
      const newWord = await createWord(dataToCopy)
      await deleteWord(word)
      return newWord
    }
  }


  const docSnap = await getDoc(docRef)

  return {word: docSnap.id, ...docSnap.data()} as WordType
}

const deleteWord = async (word: string): Promise<void> => {
  const wordsCollection  = collection(firestore, 'words')
  const docRef = doc(wordsCollection, word)
  await setDoc(docRef, {
    isDeleted: true,
  }, {merge: true})
}

const bulkCreateWords = async (words: WordType[], defaultApproved=false, defaultAutoGenerated=true): Promise<WordType[]> => {
  words.forEach(word => word.word = word.word.toLowerCase())
  const initialSkipped: WordType[] = []
  const filteredWords: WordType[] = []
  const wordsCollection  = collection(firestore, 'words')
  const snapshots = await getDocs(wordsCollection)
  const existingWords = snapshots.docs.map(doc => doc.id)
  const skipped: WordType[] = []
  const wordMap: Record<string, WordType> = {}
  for (const word of words) {
    if (wordMap[word.word]) initialSkipped.push(word)
    else if (existingWords.includes(word.word)) skipped.push(word)
    else{
      filteredWords.push(word)
      wordMap[word.word] = word
    }
  }

  console.log('skipping', skipped.length)
  console.log('writing', filteredWords.length)

  await runTransaction(firestore, async (transaction) => {
    const wordsCollection  = collection(firestore, 'words')

    for (const word of filteredWords) {
      const newDocRef = doc(wordsCollection, word.word)
      transaction.set(newDocRef, {
        ...word,
        createdBy: word.createdBy || cookieService.get('displayName'),
        createdAt: word.createdAt || new Date(),
        updatedAt: word.updatedAt || new Date(),
        isApproved: defaultApproved,
        isAutoGenerated: defaultAutoGenerated,
      })
    }
  })

  return skipped.concat(initialSkipped)
}

const WordsService = {
  listWords,
  getWord,
  createWord,
  updateWord,
  deleteWord,
  bulkCreateWords,
}

export default WordsService